import {Overlay, OverlayRef} from '@angular/cdk/overlay';
import {TemplatePortal} from '@angular/cdk/portal';
import {NgClass, NgForOf, NgIf, NgTemplateOutlet} from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component, inject,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import {MatButton, MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {BehaviorSubject, combineLatest, of, Subject, takeUntil} from 'rxjs';
import {filter} from 'rxjs/operators';
import {AcademyComponent} from "../academy.component";
import {YoutubeVideoComponent} from "../../../shared/components/youtube-video/youtube-video.component";
import {MatFormField, MatInput, MatPrefix} from "@angular/material/input";
import {TranslocoPipe} from "@ngneat/transloco";
import {AcademyDetailsComponent} from "../details/details.component";
import {Category, Course} from "../academy.types";
import {MatSlideToggleChange} from "@angular/material/slide-toggle";
import {MatSelectChange} from "@angular/material/select";
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {AcademyService} from "../academy.service";
import {CustomRecallCreator} from "../../recall/components/custom-recall-creator/custom-recall-creator-component";
import {MatDialog} from "@angular/material/dialog";
import {LearningStepViewerComponent} from "../learning-step-viewer/learning-step-viewer-component";
import {strPassesFilter} from "../../../core/utils/filter.utils";
import {MatDivider} from "@angular/material/divider";

@Component({
    selector       : 'learning-panel',
    templateUrl    : './learning-panel.component.html',
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs       : 'learningPanel',
    standalone     : true,
    imports: [
        MatButtonModule,
        NgIf,
        MatIconModule,
        MatTooltipModule,
        AcademyComponent,
        YoutubeVideoComponent,
        MatFormField,
        MatInput,
        MatPrefix,
        TranslocoPipe,
        AcademyDetailsComponent,
        NgForOf,
        NgTemplateOutlet,
        NgClass,
        MatDivider,
    ],
})
export class LearningPanelComponent implements OnInit, OnDestroy
{
    @ViewChild('learningPanelOrigin') private _learningPanelOrigin: MatButton;
    @ViewChild('learningPanel') private _learningPanel: TemplateRef<any>;
    private readonly _dialog = inject(MatDialog);

    suggestedCourses: Course[]=[];
    selectedCourse: Course;
    selectedStep: any;
    categories: Category[];
    courses: Course[];
    filteredCourses: Course[];
    filters: {
        categorySlug$: BehaviorSubject<string>;
        query$: BehaviorSubject<string>;
        hideCompleted$: BehaviorSubject<boolean>;
    } = {
        categorySlug$ : new BehaviorSubject('all'),
        query$        : new BehaviorSubject(''),
        hideCompleted$: new BehaviorSubject(false),
    };

    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _academyService: AcademyService,
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to router path changes and log them
        this._router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((event: NavigationEnd) => {
                const currentPath = event.urlAfterRedirects.split('?')[0];
                const filterStr = currentPath.replace(/\\/g, ' ').replace(/\//g, ' ').trim();

                // Re-initialize suggestedCourses if courses are already loaded
                if (this.courses) {
                    if (filterStr) {
                        this.suggestedCourses = this.courses.filter(course => this.isSuggestedCourse(course, filterStr));
                    } else {
                        this.suggestedCourses = [];
                    }
                    this._changeDetectorRef.markForCheck();
                }
            });

        // Get the categories
        this._academyService.categories$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((categories: Category[]) =>
            {
                this.categories = categories;

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

        // Get the courses
        this._academyService.courses$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((courses: Course[]) =>
            {
                this.courses = this.filteredCourses = courses;

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

        // Filter the courses
        combineLatest([this.filters.categorySlug$, this.filters.query$, this.filters.hideCompleted$])
            .subscribe(([categorySlug, query, hideCompleted]) =>
            {
                // Reset the filtered courses
                this.filteredCourses = this.courses;

                // Filter by category
                if ( categorySlug !== 'all' )
                {
                    this.filteredCourses = this.filteredCourses.filter(course => course.category === categorySlug);
                }

                // Filter by search query
                if ( query !== '' )
                {
                    this.filteredCourses = this.filteredCourses.filter(course => this.coursePassesQueryFilter(course, query));
                }

                // Filter by completed
                if ( hideCompleted )
                {
                    this.filteredCourses = this.filteredCourses.filter(course => course.progress.completed === 0);
                }
            });
    }

    private coursePassesQueryFilter(course:Course, q:string):boolean{
        return strPassesFilter(course.title, q) ||
               strPassesFilter(course.description, q) || strPassesFilter(course.description, q) ||
               strPassesFilter(course.category, q) ||
               strPassesFilter(course.keywords, q)
            ;
    }

    private isSuggestedCourse(course:Course, path:string):boolean{
        let found:boolean = false;
        if(course.bestForPages?.length > 0) {
            for (const c of course.bestForPages) {
                if (strPassesFilter(c, path)) {
                    found = true;
                    break;
                }
            }
        }
        return found;
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if ( this._overlayRef )
        {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the learning panel
     */
    openPanel(): void
    {
        // Return if the learning panel or its origin is not defined
        if ( !this._learningPanel || !this._learningPanelOrigin )
        {
            return;
        }

        // Create the overlay if it doesn't exist
        if ( !this._overlayRef )
        {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(new TemplatePortal(this._learningPanel, this._viewContainerRef));
    }

    /**
     * Close the learning panel
     */
    closePanel(): void
    {
        this._overlayRef.detach();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void
    {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop     : true,
            backdropClass   : 'fuse-backdrop-on-mobile',
            scrollStrategy  : this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                                  .flexibleConnectedTo(this._learningPanelOrigin._elementRef.nativeElement)
                                  .withLockedPosition(true)
                                  .withPush(true)
                                  .withPositions([
                                      {
                                          originX : 'start',
                                          originY : 'bottom',
                                          overlayX: 'start',
                                          overlayY: 'top',
                                      },
                                      {
                                          originX : 'start',
                                          originY : 'top',
                                          overlayX: 'start',
                                          overlayY: 'bottom',
                                      },
                                      {
                                          originX : 'end',
                                          originY : 'bottom',
                                          overlayX: 'end',
                                          overlayY: 'top',
                                      },
                                      {
                                          originX : 'end',
                                          originY : 'top',
                                          overlayX: 'end',
                                          overlayY: 'bottom',
                                      },
                                  ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

    updateSelectedCourse(course: Course){
        this.selectedCourse = course;
        this._academyService.getCourseById(course.id).subscribe(course => {
            this.selectedCourse = course;
            this._changeDetectorRef.markForCheck();
        });
    }

    updateSelectedStep(step: any){
        const dialogRef = this._dialog.open(LearningStepViewerComponent,
            {
                data: step,
                // height: "75vh",
                // width: "90vw",
                // maxHeight: "75vh",
                // maxWidth: "90vw",
            }
        );

        dialogRef.afterClosed().subscribe(result => {

        });
    }


    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Filter by search query
     *
     * @param query
     */
    filterByQuery(query: string): void
    {
        this.filters.query$.next(query);
    }

    /**
     * Filter by category
     *
     * @param change
     */
    filterByCategory(change: MatSelectChange): void
    {
        this.filters.categorySlug$.next(change.value);
    }

    /**
     * Show/hide completed courses
     *
     * @param change
     */
    toggleCompleted(change: MatSlideToggleChange): void
    {
        this.filters.hideCompleted$.next(change.checked);
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }

    printStep(step){
        console.log(step);
    }
}
